import { useEffect, useState } from 'react';
import { DEFINE_OPTION, DURATION_SECONDS } from '../../constants';
import { DefineSelector } from '../DefineSelector';
import { BottomNavigation } from '../BottomNavigation';
import { DefineInput } from '../DefineInput';
import { DefineDuration } from '../DefineDuration';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { REDUX_KEY } from '../../redux/constants';
import { getWeeksInRange } from '../../services/date';
import mapBurstToNumber from './mapBurstToNumber';
import {
  setAllRows,
  setEditableCell,
  updateImpact,
  updateTarps
} from '../../redux/features/burst/burstSlice';
import {
  EDITABLE_COLUMN,
  rowStructure
} from '../../redux/features/burst/types';
import { updateBudget } from '../../redux/features/burst/burstSlice';
import { Week } from '../../services/date/types';
import { createDefaultRow } from '../../services/table';
import { useModal } from '../../context/ModalContext';
import {
  BaseWeeklyPricingAttributes,
  getRatio,
  getWeekBasePrice,
  RATECARD
} from '../../services/calculate';

const BudgetSplitSection = () => {
  const dispatch = useAppDispatch();
  const {
    grossBudget,
    targetTarp,
    targetImpact,
    usedBudget,
    usedImpacts,
    usedTarps,
    commencingDay,
    demographic,
    advertiserDiscount: discount
  } = useAppSelector((state: RootState) => state.detail);
  const { durationLoading, weeklyPricing } = useAppSelector(
    (state: RootState) => state.spreadsheet
  );
  const [selected, setSelected] = useState<DEFINE_OPTION | undefined>(
    undefined
  );
  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail[REDUX_KEY.CAMPAIGN_LENGTH]
  );
  const { campaignDurations } = useAppSelector(
    (state: RootState) => state.detail
  );
  const [weeks, setWeeks] = useState<Week[]>([]);
  const { openModal, closeModal } = useModal();

  const handleOnChange = (selected: DEFINE_OPTION): void => {
    setSelected(selected);

    switch (selected) {
      case DEFINE_OPTION.BUDGET:
        updateEditableCell(EDITABLE_COLUMN.BUDGET);
        break;
      case DEFINE_OPTION.TARPS:
        updateEditableCell(EDITABLE_COLUMN.TARPS);
        break;
      case DEFINE_OPTION.ZEROS:
        updateEditableCell(EDITABLE_COLUMN.IMPACT);
        break;
      default:
        break;
    }
  };

  const updateEditableCell = (burst: EDITABLE_COLUMN) => {
    const editableCell = setEditableCell({ burst });
    dispatch(editableCell);
  };

  const handleSplitAcrossDurations = (): void => {
    visibleDurations.map(([burst, isBurstSelected]) => {
      const selectedBurst = burst as DURATION_SECONDS;
      if (isBurstSelected) {
        dispatch(
          updateBudget({
            budget: grossBudget / visibleDurations.length,
            burst: selectedBurst
          })
        );
        dispatch(
          updateTarps({
            tarps: targetTarp / visibleDurations.length,
            burst: selectedBurst
          })
        );
        dispatch(
          updateImpact({
            impact: targetImpact / visibleDurations.length,
            burst: selectedBurst
          })
        );
      }
    });
  };

  const handleEnterDurationsManually = (): void => {
    resetAllRows();
  };

  /**
   * Loops through all selected durations and resets the rows for each duration based
   * on their weekly CPT and CPM values and multiplaying the CPT and CPM values by their duration ratio.
   *
   * @example Calculating the duration on a 15 second burst
   *
   * Date: 09-01-2025
   * Week: 2
   * Burst: 15 seconds
   * Ratio for burst: 0.6
   * Base CPT: 100
   * Base CPM: 200
   *
   * Calculated CPT: 100 * 0.6 = 60
   * Calculated CPM: 200 * 0.6 = 120
   *
   * @returns {void}
   */
  const resetAllRows = (): void => {
    if (!demographic) return;

    Object.entries(campaignDurations).map(([burst, isBurstSelected]) => {
      if (isBurstSelected) {
        const initialRows: rowStructure[] = [];

        weeks.map((week, id) => {
          const cptAttributes: BaseWeeklyPricingAttributes = {
            demographic,
            ratecard: RATECARD.CPT,
            week: week.weekIndex,
            discount
          };

          const cpmAttributes: BaseWeeklyPricingAttributes = {
            demographic,
            ratecard: RATECARD.CPM,
            week: week.weekIndex,
            discount
          };

          const baseWeekCPT = getWeekBasePrice(weeklyPricing, cptAttributes);
          const baseWeekCPM = getWeekBasePrice(weeklyPricing, cpmAttributes);

          const modifier = getRatio(durationLoading, burst as DURATION_SECONDS);

          const cpt = baseWeekCPT * modifier;
          const cpm = baseWeekCPM * modifier;

          const row = createDefaultRow(id, cpt, cpm, week);
          initialRows.push(row);
        });

        dispatch(
          setAllRows({
            burst: burst as DURATION_SECONDS,
            rows: initialRows
          })
        );
      }
    });
  };

  const calculateWeeksCommencing = () => {
    if (!startDate || !endDate) return;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const weeks = getWeeksInRange(start, end, commencingDay);
    setWeeks(weeks);
  };

  useEffect(() => {
    calculateWeeksCommencing();
  }, [selected]);

  const visibleDurations = Object.entries(campaignDurations).filter(
    ([, isBurstSelected]) => isBurstSelected
  );

  const hasTargetBeenAssigned = () => {
    switch (selected) {
      case DEFINE_OPTION.BUDGET:
        return usedBudget !== 0;
      case DEFINE_OPTION.TARPS:
        return usedTarps !== 0;
      case DEFINE_OPTION.ZEROS:
        return usedImpacts !== 0;
    }
  };

  const handleOnClick = () => {
    let isError = false;

    if (!hasTargetBeenAssigned()) {
      openModal('ActionModal', {
        title: 'Error',
        content:
          'The selected target has not been assigned. Please assign values to at least one burst to continue.',
        okButtonText: 'OK',
        modalCallback: () => {
          closeModal();
        }
      });

      isError = true;
    }

    return isError;
  };

  return (
    <>
      <DefineSelector onChange={handleOnChange} />
      {selected !== undefined && (
        <>
          <DefineInput
            option={selected}
            visibleDurations={visibleDurations}
            onSplitAcrossDurations={handleSplitAcrossDurations}
            onEnterDurationsManually={handleEnterDurationsManually}
          />
          {Object.entries(campaignDurations).map(([burst, isBurstSelected]) => {
            const burstNumber = mapBurstToNumber(burst as DURATION_SECONDS);
            return (
              isBurstSelected && (
                <DefineDuration
                  key={burst}
                  option={selected}
                  burstLength={burstNumber}
                  weeks={weeks}
                  burstName={burst as DURATION_SECONDS}
                />
              )
            );
          })}
        </>
      )}
      <BottomNavigation
        nextTo="/fourth"
        onClick={handleOnClick}
        showBackNavigation={false}
        showNextNavigation={selected !== undefined}
      />
    </>
  );
};

export default BudgetSplitSection;
